import { useQuery } from '@apollo/client';
import { FetchPolicy } from '@constants/FetchPolicy';
import { GetAllClients as GetAllClientsType } from '@generated/types';
import { createStyles, Grid, makeStyles, Theme } from '@material-ui/core';
import React, { useEffect, useState } from 'react';

import { getMarketplaceWorkspaceIds } from '../_helpers/MarketplaceAccessHelper';
import { history } from '../_helpers/history';
import { ConfigName } from '../constants/ConfigName';
import CandidateModal from '../views/candidate/CandidateModal';

import { GetAllClients } from '../_services/clients/queries';
import { getConfigSetting } from './configuration/configuration';

import DynamicClientCard from './client-card/DynamicClientCard';

interface Props {
  location: any;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      marginTop: 40,
    },
  }),
);

export default function ClientDashboard({ location }: Props) {
  const classes = useStyles();
  const [candidateModalEnabled, setCandidateModalEnabled] = useState<boolean | null>(null);
  const [candidateModalMode, setCandidateModalMode] = useState<{
    open: boolean;
    mode: 'create' | 'edit';
    candidateId?: string;
    contractorId?: string;
    workspaceId?: string;
    noWorkspaceAccess?: boolean;
  }>({ open: false, mode: 'create' });

  const { data } = useQuery<GetAllClientsType>(GetAllClients, {
    fetchPolicy: FetchPolicy.NETWORK_ONLY,
  });

  const [marketplaceWorkspaceIds, setMarketplaceWorkspaceIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    getConfigSetting(ConfigName.CANDIDATE_MODAL_ENABLED)
      .then(setCandidateModalEnabled)
      .catch((error) => {
        console.error('Failed to load candidate modal setting', error);
      });
  }, []);

  useEffect(() => {
    async function fetchWorkspaceIds() {
      const workspaceIds = await getMarketplaceWorkspaceIds();
      setMarketplaceWorkspaceIds(workspaceIds);
    }

    fetchWorkspaceIds();
  }, []);

  useEffect(() => {
    if (!location.state?.redirect || candidateModalEnabled === null) {
      return;
    }

    const redirectParams = new URLSearchParams(location.search);
    const rehireAction = redirectParams.get('rehireAction');
    const candidateId = redirectParams.get('candidateId') ?? undefined;
    const contractorId = redirectParams.get('contractorId') ?? undefined;
    const workspaceId = redirectParams.get('workspaceId') ?? undefined;
    const noWorkspaceAccess = redirectParams.get('noWorkspaceAccess') === 'true';
    if (!['add', 'edit'].includes(rehireAction ?? '') || (rehireAction === 'edit' && !candidateId)) {
      return;
    }

    const mode = rehireAction === 'edit' ? 'edit' : 'create';

    if (candidateModalEnabled) {
      setCandidateModalMode({
        open: true,
        mode,
        candidateId,
        contractorId,
        workspaceId,
        noWorkspaceAccess,
      });
      history.replace('/');
    } else {
      const candidateFormPath =
        mode === 'edit' && candidateId ? `/editCandidate/${encodeURIComponent(candidateId)}` : '/submitCandidate';
      history.push(candidateFormPath);
    }
  }, [candidateModalEnabled, location.search, location.state]);

  return (
    <>
      <Grid container className={classes.root}>
        <Grid item xs={12}>
          <Grid container justify={'center'}>
            <Grid item xs={10}>
              {data?.getAllClients?.map((client: Readonly<any>) => {
                return (
                  <DynamicClientCard
                    key={client.id}
                    logo={client.logo}
                    name={client.name}
                    url={client.url}
                    workspaceId={client.id}
                    hasMarketplaceAccess={marketplaceWorkspaceIds.has(client.id)}
                  />
                );
              })}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {candidateModalEnabled && (
        <CandidateModal
          open={candidateModalMode.open}
          mode={candidateModalMode.mode}
          candidateId={candidateModalMode.candidateId}
          contractorId={candidateModalMode.contractorId}
          workspaceId={candidateModalMode.workspaceId}
          noWorkspaceAccess={candidateModalMode.noWorkspaceAccess}
          handleClose={() => setCandidateModalMode((current) => ({ ...current, open: false }))}
          isConfirmRehire={true}
        />
      )}
    </>
  );
}
